import React from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import dayjs from 'dayjs';
import Employee from '../../types/api/Employee';

interface TrialPeriodCardProps {
  employees: Employee[];
}

const TrialPeriodCard: React.FC<TrialPeriodCardProps> = ({ employees }) => {
  const theme = useTheme();

  // Filter employees in trial period (within 60 days of entry)
  const employeesInTrial = employees
    .filter((employee) => {
      const daysSinceEntry = dayjs().diff(dayjs(employee.entryDate), 'day');
      return daysSinceEntry >= 0 && daysSinceEntry <= 60;
    })
    .map((employee) => {
      const entryDate = dayjs(employee.entryDate);
      const endTrialDate = entryDate.add(60, 'day');
      const daysRemaining = endTrialDate.diff(dayjs(), 'day');
      
      return {
        id: employee.id,
        name: `${employee.lastName.toUpperCase()} ${employee.firstName}`,
        daysRemaining: Math.max(0, daysRemaining),
        entryDate: employee.entryDate,
      };
    })
    .sort((a, b) => a.daysRemaining - b.daysRemaining);

  const getStatusColor = (daysRemaining: number) => {
    if (daysRemaining <= 7) return theme.palette.error.main; // red
    if (daysRemaining <= 15) return theme.palette.warning.main; // orange
    return theme.palette.success.main; // green
  };

  return (
    <Box 
      className="p-4 rounded-xl border"
      sx={{ 
        backgroundColor: 'background.paper',
        borderColor: 'divider',
        color: 'text.primary'
      }}
    >
      <style>{`
        .scrollbar-thin::-webkit-scrollbar {
          height: 6px;
        }
        .scrollbar-thin::-webkit-scrollbar-track {
          background: ${theme.palette.divider};
          border-radius: 3px;
        }
        .scrollbar-thin::-webkit-scrollbar-thumb {
          background: ${theme.palette.text.secondary};
          border-radius: 3px;
        }
        .scrollbar-thin::-webkit-scrollbar-thumb:hover {
          background: ${theme.palette.text.primary};
        }
      `}</style>
      <h3 className="text-lg font-semibold mb-4">Période d'essai</h3>
      {employeesInTrial.length === 0 ? (
        <Box sx={{ color: 'text.secondary', fontSize: '0.875rem', paddingY: 2 }}>
          Aucun employé en période d'essai
        </Box>
      ) : (
        <div className="space-y-3">
          <div
            style={{
              display: 'flex',
              overflowX: 'auto',
              overflowY: 'hidden',
              gap: '12px',
              paddingBottom: '8px',
              scrollBehavior: 'smooth',
            }}
            className="scrollbar-thin"
          >
            {employeesInTrial.map((emp) => (
              <Box
                key={emp.id}
                className="flex-shrink-0 p-3 rounded-lg"
                sx={{
                  minWidth: '280px',
                  backgroundColor: 'background.default',
                }}
              >
                <div className="text-sm font-medium">{emp.name}</div>
                <Box sx={{ fontSize: '0.75rem', color: 'text.secondary', marginTop: '0.25rem' }}>
                  Entrée: {new Date(emp.entryDate).toLocaleDateString('fr-FR')}
                </Box>
                <div
                  className="mt-2 text-right px-3 py-1 rounded-full font-semibold inline-block"
                  style={{
                    backgroundColor: getStatusColor(emp.daysRemaining),
                    color: 'white',
                    fontSize: '0.875rem',
                  }}
                >
                  {emp.daysRemaining} j restants
                </div>
              </Box>
            ))}
          </div>
        </div>
      )}
    </Box>
  );
};

export default TrialPeriodCard;
